import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { tourStorage } from "@/utils/tourStorage";

export type TourId =
  | "homepage"
  | "audits"
  | "footprint"
  | "benchmarks"
  | "analytics"
  | "peerBenchmarks"
  | "importAgent"
  | "dataPageOverview"
  | "dataPageFiles"
  | "dataPageReview";

export interface TourContextType {
  activeTour: TourId | null;
  completedTours: TourId[];
  startTour: (tourId: TourId) => void;
  endTour: () => void;
  markTourComplete: (tourId: TourId) => void;
  resetTourProgress: () => void;
  hasCompletedTour: (tourId: TourId) => boolean;
  shouldAutoShowTour: (tourId: TourId) => boolean;
  // Intro video properties
  isIntroVideoActive: boolean;
  hasWatchedIntroVideo: boolean;
  showIntroVideo: () => void;
  closeIntroVideo: (startTourAfter?: boolean) => void;
  markIntroVideoWatched: () => void;
  shouldShowIntroVideo: (tourId: TourId) => boolean;
  startTourWithVideo: (tourId: TourId) => void;
}

export const TourContext =
  createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activeTour, setActiveTour] = useState<TourId | null>(null);
  const [completedTours, setCompletedTours] = useState<TourId[]>([]);
  const [isIntroVideoActive, setIsIntroVideoActive] = useState(false);
  const [hasWatchedIntroVideo, setHasWatchedIntroVideo] = useState(true); // Default true to prevent flash

  useEffect(() => {
    const stored = tourStorage.getCompletedTours();
    setCompletedTours(stored as TourId[]);
    // Load video watched state
    setHasWatchedIntroVideo(tourStorage.hasWatchedIntroVideo());
  }, []);

  const startTour = useCallback((tourId: TourId) => {
    setActiveTour(tourId);
  }, []);

  const endTour = useCallback(() => {
    setActiveTour(null);
  }, []);

  const markTourComplete = useCallback((tourId: TourId) => {
    tourStorage.setTourCompleted(tourId);
    setCompletedTours((prev) =>
      prev.includes(tourId) ? prev : [...prev, tourId]
    );
  }, []);

  const resetTourProgress = useCallback(() => {
    tourStorage.resetAllTours();
    setCompletedTours([]);
  }, []);

  const hasCompletedTour = useCallback(
    (tourId: TourId) => completedTours.includes(tourId),
    [completedTours]
  );

  const shouldAutoShowTour = useCallback(
    (tourId: TourId) => !completedTours.includes(tourId),
    [completedTours]
  );

  // Intro video methods
  const showIntroVideo = useCallback(() => {
    setIsIntroVideoActive(true);
  }, []);

  const markIntroVideoWatched = useCallback(() => {
    tourStorage.setIntroVideoWatched();
    setHasWatchedIntroVideo(true);
  }, []);

  const closeIntroVideo = useCallback(
    (startTourAfter = true) => {
      setIsIntroVideoActive(false);
      markIntroVideoWatched();
      if (startTourAfter) {
        // Small delay to allow modal to close before tour starts
        setTimeout(() => {
          startTour("homepage");
        }, 300);
      }
    },
    [markIntroVideoWatched, startTour]
  );

  const shouldShowIntroVideo = useCallback(
    (tourId: TourId) => {
      // Only show intro video for homepage, if not watched and tour not completed
      return (
        tourId === "homepage" &&
        !hasWatchedIntroVideo &&
        !completedTours.includes(tourId)
      );
    },
    [hasWatchedIntroVideo, completedTours]
  );

  const startTourWithVideo = useCallback(
    (tourId: TourId) => {
      // For homepage, always show video first (user can skip)
      if (tourId === "homepage") {
        setIsIntroVideoActive(true);
      } else {
        startTour(tourId);
      }
    },
    [startTour]
  );

  const value = useMemo(
    () => ({
      activeTour,
      completedTours,
      startTour,
      endTour,
      markTourComplete,
      resetTourProgress,
      hasCompletedTour,
      shouldAutoShowTour,
      // Intro video
      isIntroVideoActive,
      hasWatchedIntroVideo,
      showIntroVideo,
      closeIntroVideo,
      markIntroVideoWatched,
      shouldShowIntroVideo,
      startTourWithVideo,
    }),
    [
      activeTour,
      completedTours,
      startTour,
      endTour,
      markTourComplete,
      resetTourProgress,
      hasCompletedTour,
      shouldAutoShowTour,
      isIntroVideoActive,
      hasWatchedIntroVideo,
      showIntroVideo,
      closeIntroVideo,
      markIntroVideoWatched,
      shouldShowIntroVideo,
      startTourWithVideo,
    ]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTourContext = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTourContext must be used within TourProvider");
  }
  return context;
};
